import { useCallback, useEffect, useRef, useState } from "react";
import { mailbox } from "../../api/mailbox";
import { LIVE_EVENT } from "./useMailboxUnread";

// 新信的桌面通知(規格 §2.6「手機版面 + 桌面通知」)。
//
// - **本人在信箱頁按了「開啟桌面通知」**才詢問瀏覽器權限,並記住「本人要」(localStorage);
//   瀏覽器早就允許過也一樣 —— 沒按過就不跳。
// - 權限不是 granted 時**完全不碰 Notification**,連未讀數都不問。
// - 新信的判斷不另開後端:既有推播廣播 `nq-live` 時重問收件匣未讀數,**比上次多**才算有新信,
//   再抓收件匣第一頁拿最新那封未讀的寄件人與主旨。
// - **分頁在前景時不跳**(人就在看,畫面上的未讀數已經會變)。
// - 內容只有寄件人與主旨;點了把視窗叫到前面、打開信箱頁。

/** localStorage 裡「本人按過開啟」的記號。 */
export const NOTIFY_OPT_IN_KEY = "nq-mail-desktop-notify";
/** 開啟 / 關閉時廣播:常駐的監聽者重抓基準未讀數。 */
const SETTING_EVENT = "nq-mail-notify-setting";

export type NotifyState = "unsupported" | "denied" | "off" | "on";

function notificationApi(): typeof Notification | null {
  if (typeof window === "undefined") return null;
  const api = (window as unknown as { Notification?: typeof Notification }).Notification;
  return typeof api === "function" ? api : null;
}

function optedIn(): boolean {
  try {
    return window.localStorage.getItem(NOTIFY_OPT_IN_KEY) === "1";
  } catch {
    return false;
  }
}

/** 現在該不該跳:本人開過、而且瀏覽器允許。 */
function active(): boolean {
  const api = notificationApi();
  return api !== null && api.permission === "granted" && optedIn();
}

function currentState(): NotifyState {
  const api = notificationApi();
  if (!api) return "unsupported";
  if (api.permission === "denied") return "denied";
  return api.permission === "granted" && optedIn() ? "on" : "off";
}

/** 信箱頁上的開關。 */
export function useDesktopNotifySetting() {
  const [state, setState] = useState<NotifyState>(currentState);

  const enable = useCallback(async (): Promise<NotifyState> => {
    const api = notificationApi();
    if (!api) return "unsupported";
    let permission: NotificationPermission = api.permission;
    if (permission !== "granted") {
      try {
        permission = await api.requestPermission();
      } catch {
        permission = "denied";
      }
    }
    if (permission === "granted") {
      try {
        window.localStorage.setItem(NOTIFY_OPT_IN_KEY, "1");
      } catch {
        /* 存不了就只在這一頁有效 */
      }
    }
    window.dispatchEvent(new Event(SETTING_EVENT));
    const next = currentState();
    setState(next);
    return next;
  }, []);

  const disable = useCallback(() => {
    try {
      window.localStorage.removeItem(NOTIFY_OPT_IN_KEY);
    } catch {
      /* 沒有 localStorage 就算了 */
    }
    window.dispatchEvent(new Event(SETTING_EVENT));
    setState(currentState());
  }, []);

  return { state, enable, disable };
}

/** 常駐的監聽者(掛在 AppShell):有新信、分頁在背景時跳通知。`onOpen` = 打開信箱頁。 */
export function useDesktopNotify(onOpen: () => void): void {
  const open = useRef(onOpen);
  open.current = onOpen;

  useEffect(() => {
    let alive = true;
    let last: number | null = null;

    const notifyNewest = async () => {
      let newest;
      try {
        const page = await mailbox.messages({ folder: "INBOX" });
        const rows = Array.isArray(page?.items) ? page.items : [];
        // 只拿未讀的那封;第一頁沒有未讀的(已經在別的裝置讀掉了)就不跳,不拿別封充數
        newest = rows.find((one) => !one.seen && !one.deleted_on_server);
      } catch {
        return;
      }
      const api = notificationApi();
      if (!alive || !newest || !api || !active()) return;
      const shown = new api(newest.from_name || newest.from_addr || "新信", {
        body: newest.subject || "(無主旨)",
        tag: "nq-mailbox-new",
      });
      shown.onclick = () => {
        window.focus();
        open.current();
        shown.close?.();
      };
    };

    const check = async () => {
      if (!active()) {
        last = null;
        return;
      }
      let count: number;
      try {
        const got = await mailbox.unread();
        count = Math.max(0, Number(got?.inbox_unread) || 0);
      } catch {
        return;
      }
      if (!alive) return;
      const before = last;
      last = count;
      if (before === null || count <= before) return;
      if (document.visibilityState !== "hidden") return;
      await notifyNewest();
    };

    const onLive = () => void check();
    const onSetting = () => {
      last = null;
      void check();
    };
    void check(); // 基準
    window.addEventListener(LIVE_EVENT, onLive);
    window.addEventListener(SETTING_EVENT, onSetting);
    return () => {
      alive = false;
      window.removeEventListener(LIVE_EVENT, onLive);
      window.removeEventListener(SETTING_EVENT, onSetting);
    };
  }, []);
}
